// File: e1c/src/components/content_editor/VisualContentEditor.js
// REFACTORED: Corrected the handleUpdateBlock function to resolve the core state management bug,
// ensuring that data updates are consistent with how data is read and rendered.
// NEW: Added Quiz Blocks

import React, { useCallback, useMemo } from 'react';
import { Box, Button, Typography, Paper, Stack, IconButton, Chip, Tooltip, Divider } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import logger from '../../utils/logger';
import { BLOCK_TYPES } from 'constants/contentBlockTypes';

// Import Block Editor Components
import YesNoBlock from './YesNoBlock';
import ScaleBlock from './ScaleBlock';
import NumberBlock from './NumberBlock';
import OpenTextBlock from './OpenTextBlock';
import ChoiceBlock from './ChoiceBlock';
import PainMapBlockEditor from './PainMapBlockEditor';
import StandardizedAssessmentBlock from './StandardizedAssessmentBlock'; 
// --- ADD THESE IMPORTS ---
import QuizChoiceBlock from './QuizChoiceBlock'; // Assuming file is e1c/src/components/content_editor/QuizChoiceBlock.js
import QuizOpenTextBlock from './QuizOpenTextBlock'; // Assuming file is e1c/src/components/content_editor/QuizOpenTextBlock.js
// --- END OF ADDITION ---


// This calculates the array *after* all top-level imports are resolved, fixing the race condition.
const editorBlockTypes = Object.entries(BLOCK_TYPES).map(([key, value]) => ({
    type: value,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}));
// --- END OF ADDITION ---

// --- Unique ID helpers (keep local) ---
const generateUniqueId = (prefix = 'block_') =>
  `${prefix}${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

const generateValueForChoiceOption = () =>
  `val_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

const generateInternalOptionId = () =>
  `temp_opt_id_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;


const VisualContentEditor = React.memo(({
    blocks: blocksProp = [],
    onChange,
    onFocus,
    onBlur,
    focusedBlockId,
    setFocusedBlockId
}) => {

    // ... (logger.debug and blocks useMemo remain unchanged) ...
    if (typeof logger !== 'undefined' && logger.debug) {
      logger.debug("VisualContentEditor: Rendering/Re-rendering. Received blocksProp:", JSON.stringify(blocksProp, null, 2));
    } else {
      console.log("[DEBUG] VisualContentEditor: Rendering/Re-rendering. Received blocksProp:", JSON.stringify(blocksProp, null, 2));
    }

    const blocks = useMemo(() => {
        return (Array.isArray(blocksProp) ? blocksProp : []).map(block => ({
            ...block,
            id: block.id || generateUniqueId(),
            options: block.options || {},
        }));
    }, [blocksProp]);


    const handleAddBlock = useCallback((blockTypeToAdd, insertAtIndex = -1) => {
        onChange(prevBlocks => {
            let newBlockOptions = {};
            let defaultPrompt = "New Question";
            let defaultInstructionText = "";

            switch (blockTypeToAdd) {
                case BLOCK_TYPES.YES_NO:
                    // ... (no change)
                    break;
                case BLOCK_TYPES.SINGLE_CHOICE_SELECT:
                case BLOCK_TYPES.MULTI_CHOICE_SELECT:
                    // ... (no change)
                    break;
                case BLOCK_TYPES.SCALE:
                    // ... (no change)
                    break;
                case BLOCK_TYPES.OPEN_TEXT:
                    // ... (no change)
                    break;
                case BLOCK_TYPES.NUMBER:
                    // ... (no change)
                    break;
                case BLOCK_TYPES.INFO_TEXT:
                    // ... (no change)
                    break;
                case BLOCK_TYPES.PAIN_MAP:
                    // ... (no change)
                    break;
                case BLOCK_TYPES.STANDARDIZED_ASSESSMENT: 
                    // ... (no change)
                    break;
                
                // --- ADD THESE NEW CASES ---
                case BLOCK_TYPES.QUIZ_QUESTION_CHOICE:
                    newBlockOptions = {
                        choices: [{ id: generateInternalOptionId(), label: "Option 1", value: generateValueForChoiceOption(), is_correct: false, feedback: '' }],
                        isMultiSelect: false,
                        media_assets: []
                    };
                    defaultPrompt = "New Quiz Question";
                    break;
                case BLOCK_TYPES.QUIZ_QUESTION_OPEN_TEXT:
                    newBlockOptions = {
                        rows: 3,
                        placeholder: "Type your answer...",
                        media_assets: [],
                        model_answer: ""
                    };
                    defaultPrompt = "New Quiz Question (Open Text)";
                    break;
                // --- END OF ADDITION ---

                default:
                    newBlockOptions = {};
                    break;
            }

            // ... (newBlock creation and insertion logic remains unchanged) ...
            const newBlock = {
                id: generateUniqueId(),
                block_type: blockTypeToAdd,
                prompt_text: defaultPrompt,
                instruction_text: defaultInstructionText,
                options: newBlockOptions,
                order: prevBlocks.length,
                is_required: false,
            };
            if (setFocusedBlockId) setFocusedBlockId(newBlock.id);
            if (insertAtIndex > -1) {
                const newBlocks = [...prevBlocks];
                newBlocks.splice(insertAtIndex, 0, newBlock);
                // Re-assign order based on the new array sequence
                return newBlocks.map((block, idx) => ({ ...block, order: idx }));
            } else {
                // Original behavior: add to the end
                return [...prevBlocks, newBlock];
            }
        });
    }, [onChange, setFocusedBlockId]);

    // ... (handleUpdateBlock, handleMoveBlock, handleDeleteBlock, handleFocusInternal, handleBlurInternal remain unchanged) ...
    const handleUpdateBlock = useCallback((blockId, updatePayload) => {
        onChange(prevBlocks => prevBlocks.map(b => {
            if (b.id === blockId) {
                const newBlockData = { ...b };
                if (updatePayload.question !== undefined) {
                    newBlockData.prompt_text = updatePayload.question;
                }
                if (updatePayload.prompt !== undefined) {
                    newBlockData.prompt_text = updatePayload.prompt;
                }
                if (updatePayload.instruction_text !== undefined) {
                    newBlockData.instruction_text = updatePayload.instruction_text;
                }
                if (updatePayload.options) {
                    newBlockData.options = { ...newBlockData.options, ...updatePayload.options };
                }
                // --- UPDATE THIS LOGIC to handle new quiz choice structure ---
                if (updatePayload.internalChoices) {
                    // This logic now needs to handle the new fields (is_correct, feedback)
                    // from QuizChoiceBlock, as well as the old fields from ChoiceBlock.
                    newBlockData.options = {
                        ...newBlockData.options,
                        choices: updatePayload.internalChoices.map(ic => ({
                            value: ic.value || ic.id, // Ensure value is preserved or fall back to id
                            label: ic.label,
                            score: ic.score,
                            goto: ic.goto,
                            is_correct: ic.is_correct, // Will be undefined for old ChoiceBlock, which is fine
                            feedback: ic.feedback      // Will be undefined for old ChoiceBlock
                        }))
                    };
                }
                // --- END OF UPDATE ---
                if (newBlockData.hasOwnProperty('question')) delete newBlockData.question;
                if (newBlockData.hasOwnProperty('prompt')) delete newBlockData.prompt;
                return newBlockData;
            }
            return b;
        }));
    }, [onChange]);
    
    // ... (handleMoveBlock) ...
    const handleMoveBlock = useCallback((blockId, direction) => {
        onChange(prevBlocks => {
            const blocks = [...prevBlocks];
            const index = blocks.findIndex(b => b.id === blockId);
            if (index === -1) return blocks;

            const newIndex = index + direction;
            if (newIndex < 0 || newIndex >= blocks.length) return blocks;

            // Swap elements
            [blocks[index], blocks[newIndex]] = [blocks[newIndex], blocks[index]];

            // Re-assign order based on new array sequence
            return blocks.map((block, idx) => ({ ...block, order: idx }));
        });
    }, [onChange]);

    // ... (handleDeleteBlock) ...
    const handleDeleteBlock = useCallback((blockId) => {
        onChange(prevBlocks => prevBlocks
            .filter(block => block.id !== blockId)
            .map((block, index) => ({ ...block, order: index }))
        );
    }, [onChange]);

    // ... (handleFocusInternal) ...
    const handleFocusInternal = useCallback((blockId) => {
        if (onFocus) onFocus(blockId);
        if (setFocusedBlockId) setFocusedBlockId(blockId);
    }, [onFocus, setFocusedBlockId]);

    // ... (handleBlurInternal) ...
    const handleBlurInternal = useCallback(() => {
        if (onBlur) onBlur();
    }, [onBlur]);

    const renderBlock = (block, index, totalBlocks) => {
        const componentProps = {
            id: block.id,
            question: block.prompt_text || block.question,
            options: block.options,
            onChange: (updatePayload) => handleUpdateBlock(block.id, updatePayload),
            onDelete: () => handleDeleteBlock(block.id),
            onFocus: () => handleFocusInternal(block.id),
            onBlur: handleBlurInternal,
        };

        const typeFromBlock = block.block_type || block.type;
        const typeToRender = typeof typeFromBlock === 'string' ? typeFromBlock.toLowerCase() : 'unknown';

        let blockComponent;

        switch (typeToRender) {
            case BLOCK_TYPES.YES_NO:
                blockComponent = <YesNoBlock {...componentProps} />;
                break;
            case BLOCK_TYPES.SINGLE_CHOICE_SELECT:
                blockComponent = <ChoiceBlock {...componentProps} options={{...componentProps.options, isMultiSelect: false }} />;
                break;
            case BLOCK_TYPES.MULTI_CHOICE_SELECT:
                blockComponent = <ChoiceBlock {...componentProps} options={{...componentProps.options, isMultiSelect: true }} />;
                break;
            case BLOCK_TYPES.SCALE:
                blockComponent = <ScaleBlock {...componentProps} />;
                break;
            case BLOCK_TYPES.OPEN_TEXT:
                blockComponent = <OpenTextBlock {...componentProps} />;
                break;
            case BLOCK_TYPES.NUMBER:
                blockComponent = <NumberBlock {...componentProps} />;
                break;
            case BLOCK_TYPES.INFO_TEXT:
                blockComponent = <OpenTextBlock {...componentProps} promptLabel="Information Text" isInfo={true} />;
                break;
            case BLOCK_TYPES.PAIN_MAP:
                blockComponent = <PainMapBlockEditor block={block} onUpdateBlock={(updatedData) => handleUpdateBlock(block.id, updatedData)} onFocus={() => handleFocusInternal(block.id)} onBlur={handleBlurInternal} />;
                break;
            case BLOCK_TYPES.STANDARDIZED_ASSESSMENT:
                blockComponent = <StandardizedAssessmentBlock {...componentProps} />;
                break;
            
            // --- These are your new cases ---
            case BLOCK_TYPES.QUIZ_QUESTION_CHOICE:
                blockComponent = <QuizChoiceBlock {...componentProps} />;
                break;
            case BLOCK_TYPES.QUIZ_QUESTION_OPEN_TEXT:
                blockComponent = <QuizOpenTextBlock {...componentProps} />;
                break;
            // --- END OF NEW CASES ---
            
            // --- THIS IS THE CORRECTED DEFAULT CASE ---
            default:
                blockComponent = (
                    <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.main', backgroundColor: 'error.lightest' }}>
                        <Typography variant="body2" color="error.dark" gutterBottom>
                            <strong>Unknown Block Type:</strong> "{typeToRender}"
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Block ID: {block.id}
                        </Typography>
                    </Paper>
                );
                break;
            // --- END OF CORRECTION ---
        }

        // ... (The return/JSX for the block (with delete buttons etc) remains unchanged) ...
        return (
            <Box key={block.id} sx={{ position: 'relative', mb: 2, pt: 2 }}>
                <Box
                    sx={{
                        position: 'relative',
                        p: 0.5,
                        border: focusedBlockId === block.id ? '2px solid' : '1px dashed',
                        borderColor: focusedBlockId === block.id ? 'primary.main' : 'grey.400',
                        borderRadius: 1,
                    }}
                >
                    {blockComponent}
                    <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: -16, right: 8, zIndex: 1, p: 0.5, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                        <Tooltip title="Move Up">
                            <span>
                                <IconButton onClick={() => handleMoveBlock(block.id, -1)} size="small" disabled={index === 0}>
                                    <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Move Down">
                            <span>
                                <IconButton onClick={() => handleMoveBlock(block.id, 1)} size="small" disabled={index === totalBlocks - 1}>
                                    <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton onClick={() => handleDeleteBlock(block.id)} size="small">
                                <DeleteOutlineIcon fontSize="small" color="error" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
                <Divider>
                    <Tooltip title="Insert new element here">
                        <IconButton onClick={() => handleAddBlock('open_text', index + 1)} size="small">
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Divider>
            </Box>
        );
    };

    return (
        <Box>
            
            {blocks.map((block, index) => renderBlock(block, index, blocks.length))}

            <Paper variant="outlined" sx={{ p: 1.5, mt: blocks.length > 0 ? 2 : 0, backgroundColor: 'action.hover' }}>
                <Chip
                    label="Add Element:"
                    sx={{
                        mb: 1.5,
                        fontWeight: 'bold',
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                        color: 'text.primary'
                    }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {/* This mapping will NOW AUTOMATICALLY pick up the new quiz types
                      as long as their keys in BLOCK_TYPES are "QUIZ_QUESTION_CHOICE"
                      and "QUIZ_QUESTION_OPEN_TEXT".
                      
                      The `editorBlockTypes` variable we populated at the top
                      will generate "Quiz Question Choice" and "Quiz Question Open Text" buttons.
                    */}
                    {editorBlockTypes.map(bt => (
                        <Button
                            key={bt.type}
                            size="small"
                            variant={bt.type.startsWith('quiz_') ? "contained" : "outlined"} // <-- Optional: Make quiz buttons stand out
                            color={bt.type.startsWith('quiz_') ? "primary" : "inherit"}     // <-- Optional style
                            onClick={() => handleAddBlock(bt.type)}
                            sx={{ textTransform: 'capitalize' }}
                        >
                            {bt.label}
                        </Button>
                    ))}
                </Stack>
            </Paper>
        </Box>
    );
});

export default VisualContentEditor;